/**
 * Hero section — centered layout matching Merrily brand kit
 * Headline, subtitle, search bar with category/location/date, category pills
 */

import React, { useState } from 'react';
import { func } from 'prop-types';
import classNames from 'classnames';

import { OCCASIONS } from '../../../data/occasions';
import { hasUsZip } from '../../../util/usZip';

import css from './HeroSearchSection.module.css';

// Restructured 2026-05-15: pills + dropdown read from
// src/data/occasions.js. Previously mixed legacy keys (beach_day,
// themed_parties as "Dinner Party") and out-of-sync labels.
const PILL_COLORS = {
  baby_shower: '#FDDACF',
  dinner_parties_gatherings: '#D4F0E5',
  birthday: '#FEF0C7',
  bridal: '#E8D8F5',
  outdoor: '#D6ECFA',
};

// Quick-pick pills: 5 highest-traffic occasions. Full list lives in
// the dropdown below.
const PILL_KEYS = [
  'baby_shower',
  'dinner_parties_gatherings',
  'birthday',
  'bridal',
  'outdoor',
];

const CATEGORY_PILLS = PILL_KEYS.map(key => {
  const occasion = OCCASIONS.find(o => o.key === key);
  // Shorter pill copy for some entries: "Bridal & Wedding" → "Bridal",
  // "Dinner Parties & Gatherings" → "Dinner Party".
  const label =
    key === 'bridal'
      ? 'Bridal'
      : key === 'dinner_parties_gatherings'
      ? 'Dinner Party'
      : occasion?.label;
  return { key, label, color: PILL_COLORS[key] };
});

const HeroSearchSection = ({ onSearch }) => {
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [date, setDate] = useState('');
  const [locationError, setLocationError] = useState('');

  const handleSubmit = e => {
    e.preventDefault();
    // The field promises "Zip code" — hold the submit until we have one we
    // can geocode, instead of running a search that silently ignores the
    // typed location (UI/UX audit P1).
    if (location && !hasUsZip(location)) {
      setLocationError('Enter a 5-digit ZIP code');
      return;
    }
    const params = {};
    if (category) params.category = category;
    if (location) params.location = { fullName: location };
    if (date) params.dates = { start: date, end: date };
    onSearch(params);
  };

  const handleClearDate = e => {
    // Stop the click from also bubbling to the overlay date input below,
    // which would re-open the native picker right after the clear.
    e.stopPropagation();
    e.preventDefault();
    setDate('');
  };

  // Mobile UX fix: on touch devices, pressing Enter inside a single-line
  // text field shouldn't submit the whole form -- the user might be in the
  // middle of building their query, so we dismiss the soft keyboard via
  // blur() instead. On desktop, Enter-to-search is the expected behavior
  // (UI/UX audit P2: the suppression was applied to all viewports), so we
  // let the browser's default form submission run.
  const dismissKeyboardOnEnter = e => {
    if (e.key !== 'Enter') return;
    const isTouch =
      typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)')?.matches;
    if (isTouch) {
      e.preventDefault();
      e.target.blur();
    }
  };

  const handlePillClick = key => {
    onSearch({ category: key });
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <section className={css.hero}>
      <div className={css.container}>
        <h1 className={css.headline}>
          Beautiful parties, made easy.
        </h1>
        <p className={css.subtitle}>
          Rent thoughtfully designed party kits for every celebration.
        </p>

        <form onSubmit={handleSubmit} className={css.searchForm}>
          <div className={css.searchBar}>
            <div className={classNames(css.searchField, css.searchFieldOccasion)}>
              <label className={css.fieldLabel}>What are you celebrating?</label>
              <select
                className={classNames(css.fieldInput, { [css.fieldInputPlaceholder]: !category })}
                value={category}
                onChange={e => setCategory(e.target.value)}
              >
                <option value="">Select an occasion</option>
                {OCCASIONS.map(o => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={css.divider} />

            <div className={css.searchField}>
              <label className={css.fieldLabel}>Location</label>
              <input
                className={css.fieldInput}
                type="text"
                inputMode="numeric"
                placeholder="Zip code"
                value={location}
                onChange={e => {
                  setLocation(e.target.value);
                  if (locationError) setLocationError('');
                }}
                onKeyDown={dismissKeyboardOnEnter}
                aria-invalid={!!locationError}
              />
              {locationError ? <span className={css.fieldError}>{locationError}</span> : null}
            </div>

            <div className={css.divider} />

            {/*
              "When" field. Previously used a wrapper-onClick + showPicker()
              hack with a hidden input -- but on iOS Safari, programmatic
              showPicker() opens the native picker with today preselected
              and dismissing via "Done" auto-commits today (the user-
              reported "auto-selects today's date" trap). We replace that
              with an overlay pattern: a styled <span> shows "Anytime" or
              the picked date, and an opacity-0 <input type="date"> sits on
              top of the field area to capture taps directly. iOS opens
              the picker on direct user tap, NOT via showPicker(), so the
              auto-commit-on-dismiss doesn't trigger. Clear X is z-indexed
              above the overlay so taps on the X don't fall through.
            */}
            <div className={css.searchField}>
              <label className={css.fieldLabel}>When</label>
              <div className={css.dateRow}>
                <span
                  className={classNames(css.fieldInput, { [css.fieldInputPlaceholder]: !date })}
                >
                  {date || 'Anytime'}
                </span>
                <input
                  type="date"
                  className={css.dateInputOverlay}
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  min={today}
                  aria-label="Pick a date"
                />
                {date ? (
                  <button
                    type="button"
                    className={css.clearDateBtn}
                    onClick={handleClearDate}
                    aria-label="Clear date"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            </div>

            <button type="submit" className={css.searchBtn}>
              Search
            </button>
          </div>
        </form>

        <div className={css.pillRow}>
          {CATEGORY_PILLS.map(pill => (
            <button
              key={pill.key}
              type="button"
              className={css.categoryPill}
              style={{ background: pill.color }}
              onClick={() => handlePillClick(pill.key)}
            >
              {pill.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

HeroSearchSection.propTypes = {
  onSearch: func.isRequired,
};

export default HeroSearchSection;
